// frontend/src/contexts/SidebarZIndexContext.jsx
import { createContext, useState, useCallback } from 'react';

const SidebarZIndexContext = createContext();

export const SidebarZIndexProvider = ({ children }) => {
  const [sidebarRegistry, setSidebarRegistry] = useState(new Map());
  const [mostRecentSidebar, setMostRecentSidebar] = useState(null);
  const baseZIndex = 10000;
  
  const registerSidebar = useCallback((sidebarId) => {
    setSidebarRegistry(prev => {
      const newRegistry = new Map(prev);
      const currentMax = Math.max(...Array.from(prev.values()), baseZIndex - 1);
      const newZIndex = currentMax + 1;
      newRegistry.set(sidebarId, newZIndex);
      return newRegistry;
    });
    
    // Set this as the most recently opened sidebar
    setMostRecentSidebar(sidebarId);
  }, []);
  
  const unregisterSidebar = useCallback((sidebarId) => {
    setSidebarRegistry(prev => {
      const newRegistry = new Map(prev);
      newRegistry.delete(sidebarId);
      return newRegistry;
    });
    
    // If this was the most recent sidebar, clear it
    if (mostRecentSidebar === sidebarId) {
      setMostRecentSidebar(null);
    }
  }, [mostRecentSidebar]);
  
  const getSidebarZIndex = useCallback((sidebarId) => {
    const zIndex = sidebarRegistry.get(sidebarId);
    if (!zIndex) return baseZIndex;
    
    // If this is the most recently opened sidebar, ensure it has the highest z-index
    if (sidebarId === mostRecentSidebar) {
      const currentMax = Math.max(...Array.from(sidebarRegistry.values()), baseZIndex);
      return Math.max(zIndex, currentMax);
    }
    
    return zIndex;
  }, [sidebarRegistry, mostRecentSidebar]);
  
  const getHighestZIndex = useCallback(() => {
    return Math.max(...Array.from(sidebarRegistry.values()), baseZIndex);
  }, [sidebarRegistry]);
  
  return (
    <SidebarZIndexContext.Provider value={{
      registerSidebar,
      unregisterSidebar,
      getSidebarZIndex,
      getHighestZIndex
    }}>
      {children}
    </SidebarZIndexContext.Provider>
  );
};

export default SidebarZIndexContext;
